import { FormControl, FormHelperText, GridLegacy as Grid } from '@mui/material'
import { Control, Controller, FieldErrors, FieldValues, Path, PathValue } from 'react-hook-form'
import { Select } from 'ComponentLibrary'
import { GroupedOptions, SelectOption } from '../../Select/Select'
import { get } from 'lodash'
import { CSSProperties, ElementType, ReactElement, ReactNode } from 'react'

export type FormSelectFieldProps<TFieldValues extends FieldValues> = {
    label?: string
    upperOptions?: SelectOption[]
    options?: SelectOption[]
    groupedOptions?: GroupedOptions
    className?: string
    errors: FieldErrors<TFieldValues>
    disabled?: boolean
    required?: boolean
    fullWidth?: boolean
    control: Control<TFieldValues>
    fieldName: Path<TFieldValues>
    defaultValue?: PathValue<TFieldValues, Path<TFieldValues>>
    xs?: number
    multiple?: boolean
    inputFieldStyles?: CSSProperties
    onValueChange?: () => void
    renderValue?: (value: string | number) => ReactNode
    displayEmpty?: boolean
    noOptionsMessage?: string
    size?: 'small' | 'medium'
    sortedBy?: 'alphabetical'
    sortGroupOptions?: boolean
    IconComponent?: ElementType
    shrinkLabel?: boolean
    endAdornment?: ReactElement
    clear?: () => void
    requiredMessage?: string
}

export function FormSelectField<TFieldValues extends FieldValues>({
    label = '',
    upperOptions,
    options,
    groupedOptions,
    fullWidth,
    required,
    control,
    fieldName,
    defaultValue,
    renderValue,
    displayEmpty,
    className,
    disabled = false,
    xs = 6,
    multiple = false,
    inputFieldStyles,
    onValueChange,
    noOptionsMessage,
    shrinkLabel,
    errors,
    size = 'medium',
    IconComponent,
    sortedBy,
    sortGroupOptions,
    clear,
    requiredMessage = 'Pflichtfeld',
    ...rest
}: FormSelectFieldProps<TFieldValues>) {
    // Get is used to handle subfields like foo.bar.baz
    const error = get(errors, fieldName)
    const errorMessage = error?.message?.toString()
    return (
        <Grid
            xs={fullWidth ? 12 : xs}
            item
            sx={{
                '& .MuiInputLabel-root': {
                    '&[data-shrink=true]': size === 'small' ? { marginTop: '-8px' } : {},
                    '&[data-shrink=false]': size === 'small' ? { marginTop: '-14px', fontSize: '14px' } : {},
                },
            }}
        >
            <Controller
                name={fieldName}
                control={control}
                defaultValue={defaultValue}
                rules={{ required: required ? { value: true, message: requiredMessage } : undefined }}
                render={({ field, formState: { errors } }) => (
                    <FormControl style={{ width: '100%' }}>
                        <Select<string | number>
                            upperOptions={upperOptions}
                            options={options}
                            groupedOptions={groupedOptions}
                            inputLabel={label}
                            required={required && !!label}
                            shrinkLabel={shrinkLabel}
                            selectProps={{
                                variant: 'outlined',
                                renderValue,
                                IconComponent,
                                displayEmpty,
                                ...field,
                                ...{
                                    style: {
                                        textAlign: 'match-parent',
                                        height: size === 'small' ? 40 : undefined,
                                        margin: size === 'small' ? '-8px 0' : undefined,
                                        ...inputFieldStyles,
                                    },
                                },
                                MenuProps: {
                                    style: {
                                        maxHeight: '428px',
                                    },
                                },
                                error: !!errors[fieldName],
                                ...rest,
                            }}
                            onChange={(event) => {
                                field.onChange(event)
                                onValueChange?.()
                            }}
                            value={field.value ?? ''}
                            className={className}
                            disabled={disabled}
                            multiple={multiple}
                            noOptionsMessage={noOptionsMessage}
                            sortedBy={sortedBy}
                            sortGroupOptions={sortGroupOptions}
                            clear={clear}
                        />
                        <FormHelperText
                            variant="outlined"
                            sx={{ fontSize: '0.5rem', position: 'absolute', bottom: '-12px', right: '-12px' }}
                            margin="dense"
                            error
                        >
                            {errorMessage || ' '}
                        </FormHelperText>
                    </FormControl>
                )}
            />
        </Grid>
    )
}
